// PainMapPicker: the shared pain picker (lifted from mobile 2026-08-16). The
// host injects `savePainMap`, same shape as Logger/hostDeps.ts's capabilities.
//
// Gesture: tap a muscle to cycle severity none → red → yellow → none.
// Decision locked 2026-05-19 by Antenore (memory: feedback_pain_picker_tap_cycle).
// Per SIDE since PG-14 (2026-09-24, his word): tapping the right shoulder
// cycles the right shoulder only, the left keeps its own state. A part the
// lib draws without halves (head) cycles both sides together. Same gesture,
// no new control: the side comes from which half was tapped.
// No longpress menu, no action sheet. Green was dropped 1.74.0 (an unmarked
// muscle is already safe by definition); tapping a legacy green entry from
// pre-1.74.0 data lands on `null` next (indexOf('green') = -1, +1 = 0) so old
// green markings still clear cleanly with one tap.
//
// Persistence: optimistic local state for snappy UI, then a debounced call
// (600ms after the last tap) to the injected `savePainMap`. The picker is the
// only surface that writes painMap; consumers read through the host's own
// profile snapshot.

import { useCallback, useEffect, useRef, useState } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import {
  PAIN_SEVERITY_COLOURS,
  accountScope,
  compactPainSides,
  paintFromSeverity,
  painSides,
  worstPainSeverity,
  type AccountScope,
  type BodyFigure,
  type BodySide,
  type MuscleSlug,
  type PainEntry,
  type PainMap,
  type PainSeverity,
} from "@heraion/shared";

import { hiddenFromScreenReader } from "../primitives/hiddenFromScreenReader";
import { ScaleLegend } from "../primitives/ScaleLegend";
import type { UiTheme } from "../theme";
import { BodyMap } from "./BodyMap";
import { PainMuscleList } from "./PainMuscleList";
import { ZoomPanView } from "./ZoomPanView";
import { painSummary } from "./painSummary";

const CYCLE: ReadonlyArray<PainSeverity | null> = [null, "red", "yellow"];

const nextSeverity = (
  current: PainSeverity | undefined,
): PainSeverity | null => {
  const idx = CYCLE.indexOf(current ?? null);
  return CYCLE[(idx + 1) % CYCLE.length] ?? null;
};

// One tap on one muscle. With a side, only that side cycles; without one
// (a part drawn without halves) the whole entry cycles from its worse side.
// compactPainSides keeps the canonical spelling: equal sides stay a bare
// severity, so a map that only changed shape never reads as a new one.
const nextEntry = (
  entry: PainEntry | undefined,
  side: BodySide | undefined,
): PainEntry | undefined => {
  if (!side) return nextSeverity(worstPainSeverity(entry)) ?? undefined;
  const sides = painSides(entry);
  const ns = nextSeverity(sides[side]);
  if (ns === null) delete sides[side];
  else sides[side] = ns;
  return compactPainSides(sides);
};

const SAVE_DEBOUNCE_MS = 600;

// #503: the map a picker saved on unmount, until that save lands. A picker
// mounted meanwhile starts from it: the host's snapshot is still the old one.
// Only for the same account: after a sign-out it is someone else's map.
let leaving: { map: PainMap; scope: AccountScope } | null = null;

export type PainMapPickerProps = {
  theme: UiTheme;
  isDark?: boolean;
  initialPainMap: PainMap;
  figure?: BodyFigure;
  savePainMap: (next: PainMap) => Promise<void>;
};

export function PainMapPicker({
  theme,
  isDark,
  initialPainMap,
  figure,
  savePainMap,
}: PainMapPickerProps) {
  const { t } = useTranslation();

  // Seeded from the host snapshot on mount, then the picker owns it.
  const [severity, setSeverity] = useState<PainMap>(() => ({
    ...(leaving?.scope.current() ? leaving.map : initialPainMap),
  }));
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestRef = useRef(severity);
  latestRef.current = severity;

  const persist = useCallback(
    async (next: PainMap) => {
      setSaving(true);
      setSaveError(null);
      try {
        await savePainMap(next);
      } catch (err) {
        // The real error: a 400 parse rejection must not read as a network
        // blip, and this is the only surface that writes painMap.
        const e = err as { status?: number; body?: unknown; message?: string };
        const reason =
          typeof e?.status === "number"
            ? `${e.status} ${
                typeof e.body === "object" && e.body !== null
                  ? JSON.stringify(e.body).slice(0, 200)
                  : (e.message ?? "")
              }`
            : (e?.message ?? "network");
        setSaveError(`${t("bodymap.painSaveError")} (${reason})`);
      } finally {
        setSaving(false);
      }
    },
    [savePainMap, t],
  );

  const schedulePersist = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      void persist(latestRef.current);
    }, SAVE_DEBOUNCE_MS);
  }, [persist]);

  // The hosts pass `savePainMap` inline: read the latest one at unmount.
  const persistRef = useRef(persist);
  persistRef.current = persist;
  // Leaving within the debounce window still saves the last tap.
  useEffect(
    () => () => {
      if (!saveTimerRef.current) return;
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
      const mine = { map: latestRef.current, scope: accountScope() };
      leaving = mine;
      // persist() catches its own failure: this always settles.
      void persistRef.current(mine.map).then(() => {
        if (leaving === mine) leaving = null;
      });
    },
    [],
  );

  const onPress = (slug: MuscleSlug, side?: BodySide) => {
    // From `prev`, not the render's map: two quick taps both count.
    setSeverity((prev) => {
      const next = nextEntry(prev[slug], side);
      const out = { ...prev };
      if (next === undefined) delete out[slug];
      else out[slug] = next;
      return out;
    });
    schedulePersist();
  };

  const onClearAll = () => {
    if (Object.keys(severity).length === 0) return;
    setSeverity({});
    schedulePersist();
  };

  const painted = Object.keys(severity).length;
  const styles = makeStyles(theme);

  return (
    <View style={styles.card}>
      <Text style={styles.label}>{t("bodymap.painTitle")}</Text>
      <Text style={styles.help}>{t("bodymap.painHelp")}</Text>

      <PainMapFigure
        theme={theme}
        isDark={isDark}
        figure={figure}
        painMap={severity}
        onMusclePress={onPress}
      />

      {saving ? (
        <Text style={styles.statusSaving}>{t("bodymap.painSaving")}</Text>
      ) : null}
      {saveError ? (
        <Text style={styles.statusError}>{saveError}</Text>
      ) : null}

      {painted > 0 ? (
        <Pressable
          accessibilityRole="button"
          onPress={onClearAll}
          style={({ pressed }) => [
            styles.clearBtn,
            pressed && styles.clearBtnPressed,
          ]}
        >
          <Text style={styles.clearBtnText}>{t("bodymap.painClearAll")}</Text>
        </Pressable>
      ) : null}

      {/* Last, so a reader meets the save status and Clear all first. */}
      <PainMuscleList theme={theme} painMap={severity} onMusclePress={onPress} />
    </View>
  );
}

/** The dual figure painted from a pain map, with its severity legend. Pick
 *  mode when `onMusclePress` is given. Either way it is one image whose label
 *  lists the marked parts: a reader edits through PainMuscleList. */
export function PainMapFigure({
  theme,
  isDark,
  figure,
  painMap,
  onMusclePress,
}: {
  theme: UiTheme;
  isDark?: boolean;
  figure?: BodyFigure;
  painMap: PainMap;
  onMusclePress?: (slug: MuscleSlug, side?: BodySide) => void;
}) {
  const { t, i18n } = useTranslation();
  const styles = makeStyles(theme);
  // On web every path was its own stop (graphics-symbol); the image role
  // folds them into one (paths hidden below it), in pick mode too. Around
  // the figure only: the zoom buttons stay their own stops.
  const summary = {
    accessible: true,
    accessibilityRole: "image" as const,
    accessibilityLabel: painSummary(t, painMap, i18n.language),
  };
  // Pick mode fills the width and zooms (inbox 63d06cce): its muscles sit
  // under 44pt at 1x. Read-only keeps its fixed size, shrunk to its column
  // when that is narrower (the Home card's chevron shares the row).
  const [boxWidth, setBoxWidth] = useState<number | undefined>(undefined);
  const body = (fitTo?: number) => (
    <View {...summary}>
      <View {...hiddenFromScreenReader()}>
        <BodyMap
          mode={onMusclePress ? "pick" : "display"}
          view="front"
          value={paintFromSeverity(painMap)}
          theme={theme}
          isDark={isDark}
          onMusclePress={onMusclePress}
          scale={1.3}
          fitTo={fitTo}
          shrinkTo={onMusclePress ? undefined : boxWidth}
          figure={figure}
          layout="dual"
          everyView
        />
      </View>
    </View>
  );
  return (
    <>
      <View
        style={styles.bodyWrap}
        onLayout={onMusclePress ? undefined : (e) => setBoxWidth(e.nativeEvent.layout.width)}
      >
        {onMusclePress ? <ZoomPanView theme={theme}>{body}</ZoomPanView> : body()}
      </View>
      <ScaleLegend
        variant="dots"
        theme={theme}
        style={styles.legendRow}
        items={[
          { color: PAIN_SEVERITY_COLOURS.red, label: t("bodymap.painRed") },
          { color: PAIN_SEVERITY_COLOURS.yellow, label: t("bodymap.painYellow") },
        ]}
      />
    </>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    card: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
      gap: s.s3,
    },
    label: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: "600",
      letterSpacing: 0.6,
      textTransform: "uppercase",
    },
    help: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
    },
    bodyWrap: {
      alignItems: "center",
      paddingVertical: s.s2,
    },
    legendRow: {
      flexDirection: "row",
      justifyContent: "center",
      gap: s.s5,
      paddingVertical: s.s2,
    },
    statusSaving: {
      color: p.ink3,
      fontSize: ty.sizes.sm,
      fontStyle: "italic",
    },
    statusError: {
      color: p.danger,
      fontSize: ty.sizes.sm,
    },
    clearBtn: {
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      paddingVertical: s.s4,
      alignItems: "center",
      minHeight: 44,
      justifyContent: "center",
    },
    clearBtnPressed: {
      backgroundColor: p.surface2,
    },
    clearBtnText: {
      color: p.ink,
      fontSize: ty.sizes.base,
      fontWeight: "600",
    },
  });
}
